import React from "react";
import Editor from "@monaco-editor/react";
import DOMPurify from "dompurify";

interface Props {
    value: string;
    onChange: (value: string) => void;
    height?: string;
    showPreview: boolean;
    onTogglePreview: () => void;
}

const MonacoHtmlEditor: React.FC<Props> = ({ value, onChange, height = "450px", showPreview, onTogglePreview }) => {
    return (
        <div
            style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #ddd",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "#fff"
            }}
        >
            {/* HEADER WITH INTEGRATED PREVIEW ICON */}
            <div
                style={{
                    background: "#1e1e1e",
                    color: "#fff",
                    padding: "10px 16px",
                    fontSize: "14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0
                }}
            >
                <div>
                    <i className={showPreview ? "ri-eye-line me-2 text-primary" : "ri-code-s-slash-line me-2 text-primary"}></i>
                    <span className="fw-medium">{showPreview ? "Template Preview" : "HTML Template Editor"}</span>
                </div>

                {/* THE EYE ICON (Replaces "Monaco" text) */}
                <div
                    onClick={onTogglePreview}
                    style={{ cursor: "pointer" }}
                    title={showPreview ? "Switch to Editor" : "Switch to Preview"}
                >
                    <i className={showPreview ? "ri-code-line fs-5" : "ri-eye-line fs-5"}></i>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="flex-grow-1 overflow-hidden">
                {!showPreview ? (
                    <Editor
                        height={height}
                        defaultLanguage="html"
                        value={value}
                        onChange={(val) => onChange(val || "")}
                        theme="vs-light"
                        options={{
                            fontSize: 14,
                            fontFamily: "Fira Code, monospace",
                            minimap: { enabled: false },
                            wordWrap: "on",
                            automaticLayout: true,
                            scrollBeyondLastLine: false,
                            smoothScrolling: true,
                            cursorSmoothCaretAnimation: "on",
                            cursorBlinking: "smooth",
                            lineNumbers: "on",
                            glyphMargin: false,
                            folding: true,
                            lineDecorationsWidth: 10,
                            lineNumbersMinChars: 3,
                            padding: { top: 12, bottom: 12 },
                            quickSuggestions: true,
                            suggestOnTriggerCharacters: true,
                            contextmenu: true,
                            scrollbar: {
                                verticalScrollbarSize: 6,
                                horizontalScrollbarSize: 6,
                            },
                        }}
                    />
                ) : (
                    <div
                        className="h-100 w-100vh p-4"
                        style={{
                            background: "#fff",
                            overflowY: "auto"
                        }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(value, {
                                ADD_TAGS: ["style"],
                                WHOLE_DOCUMENT: true,
                                FORCE_BODY: true
                            }),
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default MonacoHtmlEditor;
